/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useRef } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import { CircularProgress, InputAdornment } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "pages/authentication/components/BasicLayout";
import colors from "assets/theme/base/colors";
import { Link } from "react-router-dom";
import { useAuth } from "pages/Auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { DEFAULT_ROUTES } from "components/common/constant";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
// import ReCAPTCHA from "react-google-recaptcha";
// import { REACT_APP_SITE_KEY } from "API/apiConstants";
import { encodePassword } from "components/CommonComponent/CommonFunction";

function Basic() {
  const navigate = useNavigate();
  const { notify } = useNotify();
  const { loginWithPassword } = useAuth();

  // const captchaRef = useRef(null);
  // const [isCaptchaSet, setIsCaptchaSet] = useState(false);
  // const [captchaToken, setCaptchaToken] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [loginInfo, setLoginInfo] = useState({
    username: { value: "", error: "" },
    password: { value: "", error: "" },
  });

  const handleChange = (field, value) => {
    setLoginInfo((prev) => ({
      ...prev,
      [field]: { ...prev[field], value, error: "" },
    }));
  };

  const validateField = (field, value) => {
    switch (field) {
      case "username":
        if (value.trim() === "") {
          return "Employee id is required.";
        }
        return "";

      case "password":
        if (value.trim() === "") {
          return "Password is required.";
        }
        return "";

      default:
        return "";
    }
  };
  const validateAllFields = () => {
    const errors = {
      username: validateField("username", loginInfo.username.value),
      password: validateField("password", loginInfo.password.value),
    };

    setLoginInfo((prev) => ({
      username: { ...prev.username, error: errors.username },
      password: { ...prev.password, error: errors.password },
    }));

    return errors.username === "" && errors.password === "";
  };

  const handleBlur = (name) => {
    const value = loginInfo[name].value.trim();
    const error = validateField(name, value);

    setLoginInfo((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value,
        error,
      },
    }));
  };

  const handleLogin = async () => {
    if (!validateAllFields()) return;

    // if (!isCaptchaSet) {
    //   notify("Captcha is mandatory", "error");
    //   return;
    // }

    setLoading(true);

    try {
      const loginData = {
        employeeId: loginInfo.username.value.trim(),
        password: encodePassword(loginInfo.password.value.trim()), 
        loginType: 2,
        // captchaToken,
      };

      const res = await loginWithPassword(loginData);

      if (res.success) {
        notify("Login successful", "success");

        const role = res.data?.role;
        const redirectPath = DEFAULT_ROUTES[role] || "/dashboard";

        navigate(redirectPath, { replace: true });
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Login Failed";
        notify(message, "error");

        // captchaRef.current?.reset();
        // setIsCaptchaSet(false);
      }
    } catch (err) {
      // captchaRef.current?.reset();
      // setIsCaptchaSet(false);

      notify(err?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <BasicLayout
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        pt: 6,
        background: "linear-gradient(180deg, #f5f6fa 0%, #e8edf7 100%)",
      }}
    >
      {/* Logo + Welcome text above the card */}
      <MDBox textAlign="center" mt={2} mb={2}>
        <MDBox mb={1}>
          <img
            src={require("../../../assets/images/brand/rpg-logo.png")}
            alt="Raychem Logo"
            style={{ width: 300, height: "auto" }}
          />
        </MDBox>

        <MDTypography variant="h4" pt={4} fontWeight="bold" color="dark">
          Welcome!
        </MDTypography>

        {/* Smaller description text */}
        <MDTypography variant="body2" color="text.secondary" mt={1}>
          Raychems RPG LHB - Labour Hour Booking System
        </MDTypography>
      </MDBox>

      <Card
        sx={{
          maxWidth: 550, // wider
          width: "100%",
          minHeight: 400, // taller
          borderRadius: 4,
          boxShadow: 15,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <MDBox textAlign="center" p={4}>
          <MDTypography
            variant="body2"
            color="text.secondary"
            mt={0.5}
            display="block"
          >
            Please login with your credentials
          </MDTypography>
        </MDBox>

        {/* Form */}
        <MDBox pt={2} pb={3} px={4} mt={0}>
          <MDBox component="form" role="form" onKeyDown={handleKeyPress}>
            {/* Username or employee id */}
            <MDBox mb={2}>
              <RequiredLabel>Employee ID</RequiredLabel>
              <MDInput
                type="email"
                placeholder="Enter your employee Id "
                fullWidth
                value={loginInfo.username.value}
                onChange={(e) => handleChange("username", e.target.value)}
                onBlur={() => handleBlur("username")}
                error={!!loginInfo.username.error}
                variant="outlined"
                inputProps={{ maxLength: 50 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon
                        fontSize="small"
                        sx={{ color: colors.deepBlue.main }}
                      >
                        person
                      </Icon>
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 },
                }}
              />
              {loginInfo.username.error && (
                <MDTypography variant="caption" color="error">
                  {loginInfo.username.error}
                </MDTypography>
              )}
            </MDBox>

            {/* Password */}
            <MDBox mb={1}>
              <RequiredLabel>Password</RequiredLabel>
              <MDInput
                type={showPassword ? "text" : "password"}
                placeholder="Please enter password"
                fullWidth
                value={loginInfo.password.value}
                onChange={(e) => handleChange("password", e.target.value)}
                onBlur={() => handleBlur("password")}
                error={!!loginInfo.password.error}
                variant="outlined"
                inputProps={{ maxLength: 16 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon
                        fontSize="small"
                        sx={{ color: colors.deepBlue.main }}
                      >
                        lock
                      </Icon>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                      >
                        <Icon fontSize="small">
                          {showPassword ? "visibility_off" : "visibility"}
                        </Icon>
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 },
                }}
              />
              {loginInfo.password.error && (
                <MDTypography variant="caption" color="error">
                  {loginInfo.password.error}
                </MDTypography>
              )}
            </MDBox>

            {/* <MDBox mt={2} mb={1} display="flex" justifyContent="start">
              <ReCAPTCHA
                sitekey={REACT_APP_SITE_KEY}
                ref={captchaRef}
                onChange={(token) => {
                  setCaptchaToken(token);
                  setIsCaptchaSet(!!token);
                }}
                onExpired={() => {
                  setCaptchaToken("");
                  setIsCaptchaSet(false);
                }}
              />
            </MDBox> */}

            {/* Login Button */}
            <MDBox mt={3} mb={1}>
              <MDButton
                variant="contained"
                fullWidth
                onClick={handleLogin}
                disabled={loading}
                // disabled={loading || !isCaptchaSet}
                color="deepBlue"
                sx={{
                  py: 1.75,
                  borderRadius: 3,
                  fontWeight: "bold",
                  fontSize: "0.95rem",
                  textTransform: "uppercase",
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Login"
                )}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      {/* Footer */}
      <MDBox
        textAlign="center"
        py={3}
        px={4}
        mt={5}
        mb={0}
        bgcolor="rgba(0,0,0,0.03)"
      >
        <MDTypography variant="body2" color="text.secondary">
          © 2026 Raychem LHB. All rights reserved.
        </MDTypography>
      </MDBox>
    </BasicLayout>
  );
}

export default Basic;
